/**
 * Banner del detalle de proyecto. Cover photo de fondo + avatar
 * circular sobrepuesto + título/tipo + acciones (Proyectos, Editar).
 *
 *   - Sin cover: gradiente brand como placeholder
 *   - Sin avatar: iniciales en burbuja primary
 *   - Botones para subir / cambiar / quitar (admin/lead via API)
 *
 * Refetcha el detalle del proyecto al subir/borrar para que la imagen
 * nueva entre con cache-bust por updatedAt.
 */
import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, ImagePlus, Trash2, UserCircle2 } from "lucide-react";
import clsx from "clsx";
import type { Project } from "@axorial/shared";
import { api } from "@/lib/api";
import { useImageBrightness, avatarBgForBrightness } from "@/hooks/useImageBrightness";

export function CoverBanner({ project }: { project: Project }) {
  const qc = useQueryClient();
  const coverInput = useRef<HTMLInputElement>(null);
  const avatarInput = useRef<HTMLInputElement>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["project", project.id] });

  const uploadImage = async (kind: "cover" | "avatar", file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/projects/${project.id}/${kind}`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) throw new Error(`upload-${kind}-failed-${res.status}`);
  };

  const coverMut = useMutation({
    mutationFn: (f: File) => uploadImage("cover", f),
    onSuccess: refresh,
  });
  const avatarMut = useMutation({
    mutationFn: (f: File) => uploadImage("avatar", f),
    onSuccess: refresh,
  });
  const removeCover = useMutation({
    mutationFn: () => api.del(`/projects/${project.id}/cover`),
    onSuccess: refresh,
  });
  const removeAvatar = useMutation({
    mutationFn: () => api.del(`/projects/${project.id}/avatar`),
    onSuccess: refresh,
  });

  // Cache-bust por updatedAt para que cambiar la imagen entre sin
  // necesitar Ctrl+Shift+R en el navegador.
  const v = project.updatedAt;
  const coverUrl = project.coverR2Key ? `/api/projects/${project.id}/cover?v=${v}` : null;
  const avatarUrl = project.avatarR2Key ? `/api/projects/${project.id}/avatar?v=${v}` : null;
  // Brillo del logo decide el fondo del avatar (logo oscuro → claro;
  // logo claro → oscuro). Sin logo: gradient saturado con iniciales.
  const avatarBrightness = useImageBrightness(avatarUrl);
  const avatarBg = avatarUrl
    ? avatarBgForBrightness(avatarBrightness)
    : "bg-gradient-to-br from-primary/40 to-secondary/40";

  return (
    <div className="relative rounded-lg overflow-hidden card-elevated pb-12">
      {/* Cover */}
      <div className="relative h-44 sm:h-52 md:h-60 group">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="absolute inset-0 size-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/35 via-secondary/25 to-accent/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-base-100/95 via-base-100/40 to-transparent" />

        {/* Acciones de cover (top-right) */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          <Link
            to="/projects"
            className="btn btn-sm bg-base-100/70 backdrop-blur hover:bg-base-100"
          >
            <ArrowLeft className="size-3.5" /> Proyectos
          </Link>
          <Link
            to="/projects/$projectId/edit"
            params={{ projectId: project.id }}
            className="btn btn-primary btn-sm"
          >
            <Pencil className="size-3.5" /> Editar
          </Link>
        </div>

        {/* Cover hover actions */}
        <div className="absolute top-3 left-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => coverInput.current?.click()}
            disabled={coverMut.isPending}
            className="btn btn-xs bg-base-100/70 backdrop-blur hover:bg-base-100"
            title={coverUrl ? "Cambiar portada" : "Subir portada"}
          >
            {coverMut.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <ImagePlus className="size-3.5" />
            )}
            {coverUrl ? "Cambiar" : "Subir portada"}
          </button>
          {coverUrl && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("¿Quitar la foto de portada?")) removeCover.mutate();
              }}
              disabled={removeCover.isPending}
              className="btn btn-xs btn-error btn-outline bg-base-100/70 backdrop-blur"
              title="Quitar portada"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
        <input
          ref={coverInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) coverMut.mutate(f);
            e.target.value = "";
          }}
        />
      </div>

      {/* Avatar overlapping the cover (bottom-left) + meta */}
      <div className="px-5 pb-5">
        <div className="relative flex items-end gap-4 -mt-12">
          {/* Avatar */}
          <div className="relative shrink-0 group/avatar">
            <div
              className={clsx(
                "size-24 sm:size-28 rounded-2xl border-4 border-base-100 shadow-lift overflow-hidden grid place-items-center",
                avatarBg,
              )}
            >
              {avatarUrl ? (
                // object-contain (no cover) para que logos anchos como
                // "GENDRA" se vean completos. Padding interno mínimo para
                // que el logo no choque con el borde redondeado.
                <img src={avatarUrl} alt="" className="size-full object-contain p-1.5" />
              ) : (
                <span className="text-white text-2xl font-bold tracking-tight">
                  {initials(project.name)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => avatarInput.current?.click()}
              disabled={avatarMut.isPending}
              className={clsx(
                "absolute -bottom-1 -right-1 size-7 rounded-full grid place-items-center",
                "bg-primary text-primary-content shadow-md hover:bg-primary/90 transition-colors",
                "opacity-0 group-hover/avatar:opacity-100 transition-opacity",
              )}
              title={avatarUrl ? "Cambiar avatar" : "Subir avatar"}
            >
              {avatarMut.isPending ? (
                <span className="loading loading-spinner loading-xs" />
              ) : avatarUrl ? (
                <Pencil className="size-3.5" />
              ) : (
                <ImagePlus className="size-3.5" />
              )}
            </button>
            {avatarUrl && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("¿Quitar avatar?")) removeAvatar.mutate();
                }}
                disabled={removeAvatar.isPending}
                className={clsx(
                  "absolute -top-1 -right-1 size-6 rounded-full grid place-items-center",
                  "bg-error text-error-content shadow-md hover:bg-error/90 transition-colors",
                  "opacity-0 group-hover/avatar:opacity-100 transition-opacity",
                )}
                title="Quitar avatar"
              >
                <Trash2 className="size-3" />
              </button>
            )}
            <input
              ref={avatarInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) avatarMut.mutate(f);
                e.target.value = "";
              }}
            />
          </div>

          {/* Meta — pegado abajo del avatar */}
          <div className="min-w-0 flex-1 pb-1">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              {project.type === "client" ? "Proyecto cliente" : "Proyecto interno"}
              {project.status && (
                <>
                  <span className="mx-1.5 text-base-content/30">·</span>
                  <span className="text-base-content/55 capitalize">{project.status}</span>
                </>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-semibold text-base-content tracking-tight mt-1">
              {project.name}
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Trick to keep UserCircle2 import lint-friendly even when unused above.
// (placeholder visual alternative, lo dejamos por si más adelante)
void UserCircle2;
